import React, { useState, useEffect } from 'react';
import Switch from 'react-switch';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const SmallBannerPage = () => {
  const [banners, setBanners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [formValues, setFormValues] = useState({
    images: null,
    link: '',
    active: false,
    startDate: '',
    endDate: '',
    price: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false); // Add filter modal state
  const [tempDateRange, setTempDateRange] = useState({ start: '', end: '' }); // Temp for modal
                  
  // Date range and stats
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [filteredStats, setFilteredStats] = useState({ count: 0, totalPrice: 0 });

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  // Init bootstrap tooltips
  useEffect(() => {
    if (window.bootstrap && window.bootstrap.Tooltip) {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        try { new window.bootstrap.Tooltip(tooltipTriggerEl, { html: true, sanitize: false }); } catch (e) { }
      });
    }
  }, [banners, showModal, showViewModal]);

  const validateImageFile = (file, requiredWidth, requiredHeight) => {
    return new Promise((resolve, reject) => {
      if (!file) return reject('No file provided');
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (!allowedTypes.includes(file.type)) return reject('Only JPG and PNG images are allowed');
      if (file.size > maxSize) return reject('Image size should be less than 2MB');
      const img = new Image();
      img.onload = () => {
        if (requiredWidth && requiredHeight) {
          if (img.width !== requiredWidth || img.height !== requiredHeight) {
            reject(`Image dimensions must be ${requiredWidth}x${requiredHeight}px`);
          } else {
            resolve(true);
          }
        } else resolve(true);
      };
      img.onerror = () => reject('Invalid image file');
      img.src = URL.createObjectURL(file);
    });
  };

  useEffect(() => {
    fetchBanners(1, searchQuery); // Always fetch all on search change
    // eslint-disable-next-line
  }, [searchQuery]);

  useEffect(() => {
    computeStats(banners, dateRange);
    // eslint-disable-next-line
  }, [banners, dateRange]);

  const fetchBanners = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const url = `/admin/api/smallbanners?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`;
      const res = await axios.get(url);
      let bannersData = [];
      let totalCount = 0;
      if (res.data.smallbanners) {
        bannersData = res.data.smallbanners;
        totalCount = res.data.totalCount || res.data.smallbanners.length;
      } else if (res.data.smallbanner) {
        bannersData = [res.data.smallbanner];
        totalCount = 1;
      } else if (Array.isArray(res.data)) {
        bannersData = res.data;
        totalCount = res.data.length;
      } else {
        bannersData = [];
        totalCount = 0;
      }
      setBanners(bannersData);
      setTotalPages(1); // Only one page, all records
      computeStats(bannersData, dateRange);
    } catch (err) {
      toastError('Failed to fetch small banners');
    } finally {
      setLoading(false);
    }
  };

  const computeStats = (bannerList = banners, range = dateRange) => {
    let filtered = bannerList;
    if (range.start && range.end) {
      const start = new Date(range.start);
      const end = new Date(range.end);
      filtered = bannerList.filter(b =>
        b.startDate && b.endDate &&
        new Date(b.startDate) >= start &&
        new Date(b.endDate) <= end
      );
    }
    const count = filtered.length;
    const totalPrice = filtered.reduce((sum, b) =>
      sum + (Number(b.price) || 0), 0
    );
    setFilteredStats({ count, totalPrice });
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      images: { required: !editingId },
      link: { required: true },
      startDate: { required: true },
      endDate: { required: true },
      price: { required: true },
    });

    // File validation (2MB, JPG/PNG)
    if (formValues.images instanceof File) {
      const maxSize = 2 * 1024 * 1024;
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (formValues.images.size > maxSize) {
        validationErrors.images = 'Image size should be less than 2MB';
      }
      if (!allowedTypes.includes(formValues.images.type)) {
        validationErrors.images = 'Only JPG and PNG images are allowed';
      }
    }

    // Date validation
    if (formValues.startDate && formValues.endDate) {
      const startDate = new Date(formValues.startDate);
      const endDate = new Date(formValues.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Only block past start date for new records. Allow editing existing records with past dates.
      if (!editingId && startDate < today) {
        validationErrors.startDate = 'Start date cannot be in the past';
      }
      if (endDate <= startDate) {
        validationErrors.endDate = 'End date must be after start date';
      }
    }

    // Price validation
    if (formValues.price && isNaN(Number(formValues.price))) {
      validationErrors.price = 'Price must be a number';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      if (formValues.images instanceof File) {
        formData.append('images', formValues.images);
      }
      formData.append('active', formValues.active);
      formData.append('link', formValues.link || '');
      formData.append('startDate', formValues.startDate || '');
      formData.append('endDate', formValues.endDate || '');
      formData.append('price', formValues.price || '');

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      let response;
      if (editingId) {
        response = await axios.put(`/admin/api/smallbanners/${editingId}`, formData, config);
        toastSuccess('Small Banner updated successfully!');
      } else {
        response = await axios.post('/admin/api/smallbanners', formData, config);
        toastSuccess('Small Banner added successfully!');
      }

      setCurrentPage(1);
      fetchBanners(1, searchQuery);
      handleCancel();
    } catch (err) {
      toastError(err.response?.data?.Message || 'Failed to save Chapter Ads');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner) => {
    setFormValues({
      images: null,
      link: banner.link || '',
      active: banner.active || false,
      startDate: banner.startDate ? banner.startDate.split('T')[0] : '',
      endDate: banner.endDate ? banner.endDate.split('T')[0] : '',
      price: banner.price || '',
    });
    setImagePreview(banner.images || '');
    setEditingId(banner._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure you want to delete this chapter ad?');
    if (result?.isConfirmed) {
      try {
        await axios.delete(`/admin/api/smallbanners/${id}`);
        toastSuccess('Chapter Ads deleted successfully!');
        setCurrentPage(1);
        fetchBanners(1, searchQuery);
      } catch (err) {
        toastError('Failed to delete Chapter Ads');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({
      images: null,
      link: '',
      active: false,
      startDate: '',
      endDate: '',
      price: '',
    });
    setImagePreview('');
    setEditingId(null);
    setErrors({});
  };

  const handleImageChange = (name, file) => {
    if (!file) {
      setFormValues(prevValues => ({ ...prevValues, [name]: null }));
      setImagePreview('');
      return;
    }
    // SmallBanner required dims: 400x220
    validateImageFile(file, 400, 220).then(() => {
      setFormValues(prevValues => ({ ...prevValues, [name]: file }));
      setImagePreview(URL.createObjectURL(file));
      if (errors.images) setErrors(prev => ({ ...prev, images: '' }));
    }).catch(err => {
      setErrors(prev => ({ ...prev, images: err }));
      setFormValues(prevValues => ({ ...prevValues, [name]: null }));
      setImagePreview('');
      toastError(err);
    });
  };

  const handleViewBanner = (banner) => {
    setSelectedBanner(banner);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const payload = {
        active: !currentStatus,
        updatedAt: new Date().toISOString(),
      };
      if (!currentStatus) payload.activatedAt = new Date().toISOString();
      else payload.deactivatedAt = new Date().toISOString();

      const config = { headers: { 'Content-Type': 'application/json' } };
      await axios.put(`/admin/api/smallbanners/${id}`, payload, config);

      toastSuccess(`Chapter Ads ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchBanners(currentPage, searchQuery);
    } catch (err) {
      toastError(err.response?.data?.Message || 'Failed to update chapter ads status');
    }
  };

  const handleViewToggle = async () => {
    if (!selectedBanner) return;
    try {
      await handleToggleStatus(selectedBanner._id, selectedBanner.active);
      setSelectedBanner(prev => prev ? { ...prev, active: !prev.active, updatedAt: new Date().toISOString() } : prev);
      fetchBanners(currentPage, searchQuery);
    } catch (err) {
      // handled in handleToggleStatus
    }
  };

  // Date Range Filter Handlers
  const handleDateFilter = () => {
    setTempDateRange(dateRange); // Open modal with current range
    setShowFilterModal(true);
  };
  const handleApplyFilter = () => {
    setDateRange(tempDateRange);
    computeStats(banners, tempDateRange);
    setShowFilterModal(false);
  };
  const handleDateReset = () => {
    setDateRange({ start: '', end: '' });
    setTempDateRange({ start: '', end: '' });
    computeStats(banners, { start: '', end: '' });
    setShowFilterModal(false);
  };

  // Add a safe image error handler to avoid accessing null siblings
  const handleImageError = (e, fallbackDisplay = 'inline') => {
    const img = e.currentTarget || e.target;
    try {
      if (img && img.style) img.style.display = 'none';
      // prefer nextElementSibling (skips text nodes); fallback to nextSibling
      const fallback = img && (img.nextElementSibling || img.nextSibling);
      if (fallback && fallback.style) {
        fallback.style.display = fallbackDisplay;
      }
    } catch (err) {
      // swallow any unexpected errors to avoid crashing the component
      // (no-op)
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Chapter Ads</h3>
      </div>

      {/* Stats and Filter Row */}
      <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap">
        {/* Stats Section */}
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div>
            <span className="fw-bold">Banners count:</span>
            <span className="ms-1">{filteredStats.count}</span>
          </div>
          <div>
            <span className="fw-bold">Total price:</span>
            <span className="ms-1">₹{filteredStats.totalPrice}</span>
          </div>
          {dateRange.start && dateRange.end && (
            <span className="text-muted ms-2" style={{ fontSize: '13px' }}>
              (filtered by date range)
            </span>
          )}
        </div>
        {/* Filter Section */}
        <div className="d-flex align-items-end gap-2 flex-wrap">
          <div>
            <label className="form-label mb-0" style={{ fontSize: '13px' }}>Start Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.start}
              onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              style={{ minWidth: 130 }}
            />
          </div>
          <div>
            <label className="form-label mb-0" style={{ fontSize: '13px' }}>End Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.end}
              onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              style={{ minWidth: 130 }}
            />
          </div>
          <button
            className="btn btn-sm btn-success"
            onClick={handleDateFilter}
            style={{ minWidth: 80 }}
          >
            Filter
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleDateReset}
            style={{ minWidth: 80 }}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Chapter Ads List</h5>
          <button
            className="btn btn-sm"
            style={{ backgroundColor: '#F94F01', color: '#fff' }}
            onClick={() => {
              setFormValues({
                images: null,
                link: '',
                active: false,
                startDate: '',
                endDate: '',
                price: '',
              });
              setImagePreview('');
              setEditingId(null);
              setErrors({});
              setShowModal(true);
            }}
          >
            + Add New Chapter Ads
          </button>
        </div>
        <div className="table-responsive">
          <div className="card-body p-0">
            <table
              className="table table-bordered table-sm mb-0"
              style={{ fontSize: '14px', lineHeight: '2' }}
            >
                <thead className="table-light">
                  <tr style={{ display: 'table-row' }}>
                  <th className="py-1 px-2">Ads Id</th>
                  <th className="py-1 px-2">Image</th>
                  <th className="py-1 px-2">Link</th>
                  <th className="py-1 px-2">Start Date</th>
                  <th className="py-1 px-2">End Date</th>
                  <th className="py-1 px-2">Price</th>
                  <th className="py-1 px-2">Status</th>
                  <th className="py-1 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : banners.length > 0 ? (
                  banners.map((banner, idx) => (
                    <tr key={banner._id}>
                      <td className="py-1 px-2">{idx + 1}</td>
                      <td className="py-1 px-2">{banner.bannerId || banner._id}</td>
                      <td className="py-1 px-2">
                        {banner.images && banner.images.trim() !== '' ? (
                          <img
                            src={banner.images}
                            alt="Chapter Ads"
                            style={{
                              width: '60px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(banner.images, '_blank')}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            data-bs-html="true"
                            title={`<div style="text-align:center;"><img src="${banner.images}" style="max-width:220px;max-height:140px;display:block;margin-bottom:6px;border-radius:4px;"/> <div style="font-size:12px;color:#666">${banner.createdAt ? 'Added: ' + new Date(banner.createdAt).toLocaleString() : 'Added: -'}<br/>${banner.updatedAt ? 'Edited: ' + new Date(banner.updatedAt).toLocaleString() : 'Edited: -'}</div></div>`}
                            onError={(e) => handleImageError(e, 'inline')}
                          />
                        ) : null}
                        {(!banner.images || banner.images.trim() === '') && (
                          <span className="text-muted">No Image</span>
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {banner.link ? (
                          <a
                            href={banner.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none"
                            style={{ fontSize: '12px' }}
                            title={banner.link}
                          >
                            {banner.link.length > 30 ? banner.link.substring(0, 30) + '...' : banner.link}
                          </a>
                        ) : (
                          <span className="text-muted">No Link</span>
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {banner.startDate ? (
                          <span style={{ fontSize: '12px' }}>
                            {new Date(banner.startDate).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {banner.endDate ? (
                          <span style={{ fontSize: '12px' }}>
                            {new Date(banner.endDate).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {banner.price !== undefined && banner.price !== null && banner.price !== ''
                          ? banner.price
                          : <span className="text-muted">-</span>}
                      </td>
                      <td className="py-1 px-2">
                        <div title={`Click to ${banner.active ? 'deactivate' : 'activate'}\nCreated: ${banner.createdAt ? new Date(banner.createdAt).toLocaleString() : '-'}\nUpdated: ${banner.updatedAt ? new Date(banner.updatedAt).toLocaleString() : '-'}\nActivated: ${banner.activatedAt ? new Date(banner.activatedAt).toLocaleString() : '-'}\nDeactivated: ${banner.deactivatedAt ? new Date(banner.deactivatedAt).toLocaleString() : '-'}`}>
                          <Switch
                            onChange={() => handleToggleStatus(banner._id, banner.active)}
                            checked={!!banner.active}
                            onColor="#28a745"
                            offColor="#6c757d"
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={20}
                            width={44}
                            handleDiameter={18}
                          />
                        </div>
                          {banner.updatedAt && (
                            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: 4 }}>
                              Updated: {new Date(banner.updatedAt).toLocaleString()}
                            </div>
                          )}
                      </td>
                      <td className="py-1 px-2">
                        <button
                          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0"
                          style={{ color: '#F94F01' }}
                          title="Edit"
                          onClick={() => handleEdit(banner)}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                          title="Delete"
                          onClick={() => handleDelete(banner._id)}
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-2">No Chapter Ads found.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Remove pagination controls */}
          </div>
        </div>
      </div>

      {/* Modal for add/edit */}
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Chapter Ads' : 'Add Chapter Ads'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancel}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  {/* Image Upload */}
                  <div className="mb-3">
                    <label className="form-label">Chapter Ads Image <span className="text-danger">*</span>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm rounded-pill px-2 py-0"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-html="true"
                        title={`You may upload one image. Allowed: jpg, jpeg, png. Max 2MB. Required size: 400x220 px.`}
                      >
                        ?
                      </button>
                      </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="file"
                        className={`form-control ${errors.images ? 'is-invalid' : ''}`}
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          handleImageChange('images', file);
                        }}
                      />
                      
                    </div>
                    {errors.images && <div className="invalid-feedback">{errors.images}</div>}
                    {imagePreview && (
                      <div className="mt-2">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                          className="img-thumbnail"
                        />
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Chapter Ads Link <span className="text-danger">*</span></label>
                    <input
                      type="url"
                      className={`form-control ${errors.link ? 'is-invalid' : ''}`}
                      placeholder="Enter Chapter Ads URL (e.g., https://example.com)"
                      value={formValues.link}
                      onChange={(e) => {
                        setFormValues(prev => ({ ...prev, link: e.target.value }));
                        if (errors.link) {
                          setErrors(prev => ({ ...prev, link: '' }));
                        }
                      }}
                    />
                    {errors.link && <div className="invalid-feedback">{errors.link}</div>}
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Start Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                        value={formValues.startDate}
                        onChange={(e) => {
                          setFormValues(prev => ({ ...prev, startDate: e.target.value }));
                          if (errors.startDate) {
                            setErrors(prev => ({ ...prev, startDate: '' }));
                          }
                        }}
                      />
                      {errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">End Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                        value={formValues.endDate}
                        onChange={(e) => {
                          setFormValues(prev => ({ ...prev, endDate: e.target.value }));
                          if (errors.endDate) {
                            setErrors(prev => ({ ...prev, endDate: '' }));
                          }
                        }}
                      />
                      {errors.endDate && <div className="invalid-feedback">{errors.endDate}</div>}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Price <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                      placeholder="Enter price"
                      value={formValues.price}
                      onChange={(e) => {
                        setFormValues(prev => ({ ...prev, price: e.target.value }));
                        if (errors.price) {
                          setErrors(prev => ({ ...prev, price: '' }));
                        }
                      }}
                      readOnly={!!editingId}
                    />
                    {errors.price && <div className="invalid-feedback">{errors.price}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label d-block">Active Status</label>
                    <div>
                      <Switch
                        onChange={(checked) => setFormValues(prev => ({ ...prev, active: checked }))}
                        checked={!!formValues.active}
                        onColor="#28a745"
                        offColor="#6c757d"
                        uncheckedIcon={false}
                        checkedIcon={false}
                        height={20}
                        width={44}
                        handleDiameter={18}
                      />
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: '#F94F01', color: 'white' }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update' : 'Save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Small Banner Modal */}
      {showViewModal && selectedBanner && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Chapter Ads Preview</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowViewModal(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <div className="mb-3">
                  {selectedBanner.images && selectedBanner.images.trim() !== '' ? (
                    <img
                      src={selectedBanner.images}
                      alt="Chapter Ads"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '500px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => handleImageError(e, 'block')}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center bg-light"
                      style={{
                        width: '100%',
                        height: '300px',
                        border: '2px dashed #ccc',
                        borderRadius: '8px'
                      }}
                    >
                      <span className="text-muted">No Image Available</span>
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Status:</strong>
                      <span className={`ms-2 badge ${selectedBanner.active ? 'badge-success' : 'badge-secondary'}`}>
                        {selectedBanner.active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                    <div style={{ marginTop: 6 }}>
                      <button
                        type="button"
                        className={`btn btn-sm ${selectedBanner.active ? 'btn-outline-danger' : 'btn-success'}`}
                        onClick={handleViewToggle}
                      >
                        {selectedBanner.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Created:</strong> {selectedBanner.createdAt ? new Date(selectedBanner.createdAt).toLocaleString() : '-'}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Updated:</strong> {selectedBanner.updatedAt ? new Date(selectedBanner.updatedAt).toLocaleString() : '-'}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 mb-2">
                    <p><strong>Ads Id:</strong>
                      <span className="ms-2">
                        {selectedBanner.bannerId || selectedBanner._id}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 mb-2">
                    <p><strong>Link:</strong>
                      {selectedBanner.link ? (
                        <a href={selectedBanner.link} target="_blank" rel="noopener noreferrer" className="ms-2 text-primary">
                          {selectedBanner.link}
                        </a>
                      ) : (
                        <span className="ms-2 text-muted">No link provided</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Start Date:</strong>
                      <span className="ms-2">
                        {selectedBanner.startDate ? new Date(selectedBanner.startDate).toLocaleDateString() : 'Not set'}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>End Date:</strong>
                      <span className="ms-2">
                        {selectedBanner.endDate ? new Date(selectedBanner.endDate).toLocaleDateString() : 'Not set'}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <p><strong>Price:</strong>
                      <span className="ms-2">
                        {selectedBanner.price !== undefined && selectedBanner.price !== null && selectedBanner.price !== ''
                          ? selectedBanner.price
                          : 'Not set'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
                {selectedBanner.link && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => window.open(selectedBanner.link, '_blank')}
                  >
                    Visit Link
                  </button>
                )}
                {selectedBanner.images && selectedBanner.images.trim() !== '' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => window.open(selectedBanner.images, '_blank')}
                  >
                    Open Image
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.7)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Filter by Date Range</h5>
                <button type="button" className="btn-close" onClick={() => setShowFilterModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={tempDateRange.start}
                    onChange={e => setTempDateRange(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={tempDateRange.end}
                    onChange={e => setTempDateRange(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowFilterModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDateReset}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleApplyFilter}
                  disabled={!tempDateRange.start || !tempDateRange.end}
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmallBannerPage;